import React, {
  FC,
  forwardRef,
  memo,
  useState,
  useEffect,
  useRef,
} from "react";
import {
  Autocomplete,
  Checkbox,
  Chip,
  createFilterOptions,
  Radio,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Controller, RegisterOptions } from "react-hook-form";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CustomTooltip from "./CustomTooltip";
import useDebounce from "../hooks/useDebounce";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";

interface InputAutoCompleteProps {
  control: any;
  name: string;
  rules?: Omit<
    RegisterOptions<any, string>,
    "disabled" | "setValueAs" | "valueAsNumber" | "valueAsDate"
  >;
  label?: string;
  placeholder?: string;
  options: {
    key: string | number;
    value: string;
    // Shown instead of `value` on the selected chip / removable tag — lets a
    // dropdown option carry more detail (e.g. a role suffix) than the
    // compact selected representation should. Falls back to `value`.
    chipLabel?: string;
    isCount?: boolean;
    count?: number;
  }[];
  isDisabled?: boolean;
  isError?: boolean;
  isMulti?: boolean;
  enableDynamicSearch?: boolean;
  onSearch?: (value: string) => void;
  isRequired?: boolean;
  maxSelections?: number;
  onMaxSelectionExceeded?: (max: number) => void;
}

type Opt = {
  key: string | number;
  value: string;
  chipLabel?: string;
  isCount?: boolean;
  count?: number;
};

const keyOf = (k: unknown) => String(k);

const buildKeySet = (base: Opt[]) => new Set(base.map((o) => keyOf(o.key)));

const getCached = (cache: Record<string, Opt>, k: unknown) => cache[keyOf(k)];

const isOpt = (v: Opt | undefined): v is Opt => Boolean(v);

const mergeMissingMulti = (
  base: Opt[],
  selectedKeys: unknown[],
  baseKeySet: Set<string>,
  cache: Record<string, Opt>
): Opt[] => {
  const missing = selectedKeys
    .map(keyOf)
    .filter((k) => !baseKeySet.has(k))
    .map((k) => getCached(cache, k))
    .filter(isOpt);

  return missing.length ? [...base, ...missing] : base;
};

const mergeMissingSingle = (
  base: Opt[],
  selectedKey: unknown,
  baseKeySet: Set<string>,
  cache: Record<string, Opt>
): Opt[] => {
  if (selectedKey == null) return base;

  const sk = keyOf(selectedKey);
  if (baseKeySet.has(sk)) return base;

  const cached = getCached(cache, sk);
  return cached ? [...base, cached] : base;
};

export const buildMergedOptions = (
  base: Opt[],
  value: unknown,
  isMulti: boolean,
  cache: Record<string, Opt>
): Opt[] => {
  const baseKeySet = buildKeySet(base);

  if (isMulti) {
    const selectedKeys = Array.isArray(value) ? value : [];
    return mergeMissingMulti(base, selectedKeys, baseKeySet, cache);
  }

  return mergeMissingSingle(base, value, baseKeySet, cache);
};

export const buildSelectedValue = (
  mergedOptions: Opt[],
  value: unknown,
  isMulti: boolean
): Opt[] | Opt | null => {
  if (isMulti) {
    const keys = Array.isArray(value) ? value : [];
    return mergedOptions.filter((o) => keys.includes(o.key));
  }
  return mergedOptions.find((o) => o.key === value) ?? null;
};

interface OptionLabelProps {
  value: string;
  isCount?: boolean;
  count?: number;
}

const OptionLabel = memo(({ value, isCount, count }: OptionLabelProps) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, [value]);

  const textEl = (
    <div
      ref={textRef}
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        flex: 1,
      }}
    >
      {value}
    </div>
  );

  return (
    <div
      style={{
        display: "flex",
        width: "calc(100% - 40px)",
        alignItems: "center",
      }}
    >
      {isTruncated ? (
        <CustomTooltip title={value}>{textEl}</CustomTooltip>
      ) : (
        textEl
      )}
      {isCount && (
        <span
          style={{
            marginLeft: 8,
            borderRadius: 100,
            border: "1px solid #5921F2",
            backgroundColor: "#FFFFFF",
            color: "#5921F2",
            fontSize: 11,
            padding: "1px 6px",
            flexShrink: 0,
          }}
        >
          {count ?? 0}
        </span>
      )}
    </div>
  );
});

OptionLabel.displayName = "OptionLabel";
const MAX_VISIBLE_OPTIONS = 8;
const INITIAL_RENDER_OPTIONS = 50;
const RENDER_OPTIONS_STEP = 50;
const ITEM_SIZE = 50;

const InputAutoComplete: FC<InputAutoCompleteProps> = ({
  control,
  name,
  rules,
  label,
  placeholder = "Select..",
  options,
  isDisabled,
  isError,
  isMulti = true,
  enableDynamicSearch = false,
  isRequired = false,
  onSearch,
  maxSelections,
  onMaxSelectionExceeded,
}) => {
  const theme = useTheme();

  const [searchQuery, setSearchQuery] = useState<string>("");

  const [visibleOptionLimit, setVisibleOptionLimit] = useState(
    INITIAL_RENDER_OPTIONS
  );

  const [optionCache, setOptionCache] = useState<Record<string, Opt>>({});

  // keep cache updated whenever options change
  useEffect(() => {
    if (!options?.length) return;

    setOptionCache((prev) => {
      const next = { ...prev };
      let changed = false;

      options.forEach((o) => {
        const k = String(o.key);
        if (!k) return;

        if (
          !next[k] ||
          next[k].value !== o.value ||
          next[k].count !== o.count
        ) {
          next[k] = o;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [options]);

  const debouncedSearch = useDebounce(searchQuery, 300);

  // holds a label set programmatically (option selected / form reset) that
  // should be displayed in the input but never sent to the server as a search
  const suppressSearchValueRef = useRef<string | null>(null);

  useEffect(() => {
    if (!onSearch) return;
    if (suppressSearchValueRef.current === debouncedSearch) {
      suppressSearchValueRef.current = null;
      return;
    }
    onSearch(debouncedSearch);
  }, [debouncedSearch, onSearch]);
  useEffect(() => {
    setVisibleOptionLimit(INITIAL_RENDER_OPTIONS);
  }, [debouncedSearch]);

  return (
    <Controller
      control={control}
      rules={rules}
      name={name}
      render={({ field: { value, onChange }, fieldState }) => {
        const errorMessage = fieldState?.error?.message;
        const base: Opt[] = options ?? [];

        const mergedOptions = buildMergedOptions(
          base,
          value,
          isMulti,
          optionCache
        );
        const selectedValue = buildSelectedValue(mergedOptions, value, isMulti);

        const renderFinalLabel = () => {
          if (!isRequired) return label;
          return (
            <span>
              {label}
              <span style={{ color: "red" }}>*</span>
            </span>
          );
        };

        const isOptionEqual = (option: Opt, v: Opt) => option.key === v.key;

        const handleInputChange = (
          _: any,
          newInputValue: string,
          reason: string
        ) => {
          if (reason === "reset") {
            if (newInputValue === "") {
              // MUI fires a spurious empty "reset" input-change on almost every
              // re-render while the user is actively typing (distinct from the
              // "clear" reason the (x) button uses). Multi-select fields render
              // selections as chips rather than inline text, and selections
              // already clear the search text explicitly via handleChange, so
              // ignoring empty resets there just stops that spurious event from
              // wiping in-progress text. Single-select fields show the selected
              // value's label inline, so a reset to empty (e.g. after a form
              // reset, or clearing the selection) must still clear the box to
              // stay in sync with the actual value.
              if (!isMulti) setSearchQuery("");
              return;
            }
            suppressSearchValueRef.current = newInputValue;
            setSearchQuery(newInputValue);
            return;
          }
          suppressSearchValueRef.current = null;
          setSearchQuery(newInputValue);
        };

        const handleChange = (_: any, newValue: any) => {
          if (!isMulti) {
            onChange(
              newValue && !Array.isArray(newValue) ? newValue.key : null
            );
            return;
          }

          const arr = Array.isArray(newValue) ? newValue : [];
          if (maxSelections && arr.length > maxSelections) {
            if (onMaxSelectionExceeded) onMaxSelectionExceeded(maxSelections);
            return;
          }

          onChange(arr.map((opt: Opt) => opt.key));
          setSearchQuery("");
        };
        const handleListboxScroll = (
          event: React.UIEvent<HTMLUListElement>
        ) => {
          const listboxNode = event.currentTarget;

          const isNearBottom =
            listboxNode.scrollTop + listboxNode.clientHeight >=
            listboxNode.scrollHeight - ITEM_SIZE;

          if (!isNearBottom) return;

          setVisibleOptionLimit((prev) => {
            if (prev >= mergedOptions.length) return prev;
            return prev + RENDER_OPTIONS_STEP;
          });
        };
        const handleOpen = () => {
          setVisibleOptionLimit(INITIAL_RENDER_OPTIONS);
        };

        return (
          <Autocomplete
            disableListWrap
            multiple={isMulti}
            options={mergedOptions}
            onOpen={handleOpen}
            filterOptions={(opts, params) => {
              const filtered = enableDynamicSearch
                ? opts
                : createFilterOptions<Opt>()(opts, params);

              return filtered.slice(0, visibleOptionLimit);
            }}
            disabled={isDisabled}
            isOptionEqualToValue={isOptionEqual}
            getOptionLabel={(option) => option.value}
            value={selectedValue as any}
            inputValue={searchQuery}
            onInputChange={handleInputChange}
            onChange={handleChange}
            disableCloseOnSelect={isMulti}
            renderTags={(tagValue: Opt[], getTagProps) =>
              tagValue.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip key={key} label={option.chipLabel ?? option.value} {...tagProps} />
                );
              })
            }
            renderOption={(props, option, { selected }) => (
              <li
                {...props}
                key={option.key}
                style={{
                  ...props.style,
                  backgroundColor: "#FFFFFF",
                  minHeight: ITEM_SIZE,
                }}
              >
                {isMulti ? (
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                    sx={{
                      color: "#000000",
                      "&.Mui-checked": { color: "#65558F" },
                    }}
                  />
                ) : (
                  <Radio
                    checked={selected}
                    icon={<RadioButtonUncheckedIcon fontSize="small" />}
                    checkedIcon={<RadioButtonCheckedIcon fontSize="small" />}
                    sx={{
                      mr: 1,
                      p: 0,
                      color: "#65558F",
                      "&.Mui-checked": { color: "#65558F" },
                    }}
                  />
                )}

                <OptionLabel
                  value={option.value}
                  isCount={option.isCount}
                  count={option.count}
                />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={renderFinalLabel()}
                error={!!errorMessage || isError}
                color={!!errorMessage || isError ? "error" : "primary"}
                placeholder={placeholder}
                sx={{
                  mb: 3,
                  "& .MuiInputLabel-root": {
                    fontSize: "16px",
                    color: "#1D1B20",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#1D1B20",
                  },
                  "& .MuiInputLabel-root.Mui-error": {
                    color: "#d32f2f",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                    "& fieldset": {
                      border: "1px solid #1D1B20",
                    },
                    "&:hover fieldset": {
                      border: "1px solid #1D1B20",
                    },
                    "&.Mui-focused fieldset": {
                      border: "1px solid #1D1B20",
                    },
                    "&.Mui-error fieldset": {
                      border: `1px solid ${theme.palette.custom.foreground.attention}`,
                    },
                    "&.Mui-focused.Mui-error fieldset": {
                      border: `1px solid ${theme.palette.custom.foreground.attention}`,
                    },
                  },
                }}
              />
            )}
            slotProps={{
              listbox: {
                component: ListboxComponent,
                onScroll: handleListboxScroll,
                sx: { overflowX: "hidden !important" },
              },
            }}
          />
        );
      }}
    />
  );
};

const ListboxComponent = forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, style, ...other } = props;

  return (
    <ul
      ref={ref}
      {...other}
      style={{
        ...style,
        margin: 0,
        padding: 0,
        maxHeight: MAX_VISIBLE_OPTIONS * ITEM_SIZE,
        overflowY: "auto",
        overflowX: "hidden",
        listStyle: "none",
      }}
    >
      {children}
    </ul>
  );
});

ListboxComponent.displayName = "ListboxComponent";

export default InputAutoComplete;
